import { apiClient } from '@/lib/api'
import type {
  AudienceEntry,
  AudiencePreview,
  Attribution,
  Campaign,
  CampaignChannel,
  CampaignRecipient,
  CampaignTemplate,
  CustomerInterest,
  InterestWriteBody,
  Lead,
  LeadWriteBody,
  MarketingOverview,
  Referral,
  ReferralWriteBody,
  Segment,
  Sequence,
  SequenceEnrollment,
  SequenceWriteBody,
  SegmentRules,
} from '@/types/marketing'

// --- Segments ---

export const fetchSegments = () => apiClient<Segment[]>('/marketing/segments')

export const createSegment = (body: Omit<Segment, 'id' | 'created_at' | 'updated_at'>) =>
  apiClient<Segment>('/marketing/segments', { method: 'POST', body })

export const updateSegment = (
  id: string,
  body: Omit<Segment, 'id' | 'created_at' | 'updated_at'>,
) => apiClient<Segment>(`/marketing/segments/${id}`, { method: 'PUT', body })

export const deleteSegment = (id: string) =>
  apiClient<void>(`/marketing/segments/${id}`, { method: 'DELETE' })

export const previewSegment = (rules: SegmentRules) =>
  apiClient<AudiencePreview>('/marketing/segments/preview', { method: 'POST', body: { rules } })

// --- Campaign templates ---

export const fetchCampaignTemplates = () => apiClient<CampaignTemplate[]>('/marketing/templates')

export const createCampaignTemplate = (body: {
  name: string
  channel: CampaignChannel
  subject: string
  body: string
  trigger_type?: string
}) => apiClient<CampaignTemplate>('/marketing/templates', { method: 'POST', body })

export const updateCampaignTemplate = (
  id: string,
  body: {
    name: string
    channel: CampaignChannel
    subject: string
    body: string
    trigger_type?: string
  },
) => apiClient<CampaignTemplate>(`/marketing/templates/${id}`, { method: 'PUT', body })

export const deleteCampaignTemplate = (id: string) =>
  apiClient<void>(`/marketing/templates/${id}`, { method: 'DELETE' })

// --- Campaigns ---

export const fetchCampaigns = () => apiClient<Campaign[]>('/marketing/campaigns')

export const fetchCampaignById = (id: string) => apiClient<Campaign>(`/marketing/campaigns/${id}`)

export const fetchCampaignRecipients = (id: string) =>
  apiClient<CampaignRecipient[]>(`/marketing/campaigns/${id}/recipients`)

export const createCampaign = (body: { name: string; template_id: string; segment_id: string }) =>
  apiClient<Campaign>('/marketing/campaigns', { method: 'POST', body })

export const sendCampaign = (id: string) =>
  apiClient<Campaign>(`/marketing/campaigns/${id}/send`, { method: 'POST' })

export const scheduleCampaign = (id: string, scheduled_at: string) =>
  apiClient<Campaign>(`/marketing/campaigns/${id}/schedule`, {
    method: 'POST',
    body: { scheduled_at },
  })

export const cancelCampaign = (id: string) =>
  apiClient<Campaign>(`/marketing/campaigns/${id}/cancel`, { method: 'POST' })

export const deleteCampaign = (id: string) =>
  apiClient<void>(`/marketing/campaigns/${id}`, { method: 'DELETE' })

// --- Audience (channel consent) ---

export const fetchAudience = () => apiClient<AudienceEntry[]>('/marketing/audience')

export const setConsent = (customerId: string, channel: CampaignChannel, opt_in: boolean) =>
  apiClient<{ customer_id: string; email_opt_in: boolean; sms_opt_in: boolean }>(
    `/marketing/audience/${customerId}`,
    { method: 'PATCH', body: { channel, opt_in } },
  )

// --- Interests (inventory alerts) ---

export const fetchInterests = () => apiClient<CustomerInterest[]>('/marketing/interests')

export const createInterest = (body: InterestWriteBody) =>
  apiClient<CustomerInterest>('/marketing/interests', { method: 'POST', body })

export const deleteInterest = (id: string) =>
  apiClient<void>(`/marketing/interests/${id}`, { method: 'DELETE' })

// --- Nurture sequences ---

export const fetchSequences = () => apiClient<Sequence[]>('/marketing/sequences')

export const fetchSequenceEnrollments = (id: string) =>
  apiClient<SequenceEnrollment[]>(`/marketing/sequences/${id}/enrollments`)

export const createSequence = (body: SequenceWriteBody) =>
  apiClient<Sequence>('/marketing/sequences', { method: 'POST', body })

export const updateSequence = (id: string, body: SequenceWriteBody) =>
  apiClient<Sequence>(`/marketing/sequences/${id}`, { method: 'PUT', body })

export const deleteSequence = (id: string) =>
  apiClient<void>(`/marketing/sequences/${id}`, { method: 'DELETE' })

export const enrollCustomer = (sequenceId: string, customerId: string) =>
  apiClient<SequenceEnrollment>(`/marketing/sequences/${sequenceId}/enrollments`, {
    method: 'POST',
    body: { customer_id: customerId },
  })

// --- Overview ---

export const fetchMarketingOverview = (months = 12) =>
  apiClient<MarketingOverview>('/marketing/overview', { params: { months: String(months) } })

// --- Leads (prospect intake) ---

export const fetchLeads = () => apiClient<Lead[]>('/marketing/leads')

export const createLead = (body: LeadWriteBody) =>
  apiClient<Lead>('/marketing/leads', { method: 'POST', body })

export const updateLead = (id: string, body: LeadWriteBody) =>
  apiClient<Lead>(`/marketing/leads/${id}`, { method: 'PUT', body })

export const convertLead = (id: string) =>
  apiClient<{ customer_id: string }>(`/marketing/leads/${id}/convert`, { method: 'POST' })

export const deleteLead = (id: string) =>
  apiClient<void>(`/marketing/leads/${id}`, { method: 'DELETE' })

// --- Referrals ---

export const fetchReferrals = () => apiClient<Referral[]>('/marketing/referrals')

export const createReferral = (body: ReferralWriteBody) =>
  apiClient<Referral>('/marketing/referrals', { method: 'POST', body })

export const updateReferral = (id: string, body: ReferralWriteBody) =>
  apiClient<Referral>(`/marketing/referrals/${id}`, { method: 'PUT', body })

export const deleteReferral = (id: string) =>
  apiClient<void>(`/marketing/referrals/${id}`, { method: 'DELETE' })

// --- Lead-source attribution ---

export const fetchAttribution = (months = 12) =>
  apiClient<Attribution>('/marketing/attribution', { params: { months: String(months) } })
